import { CreditCardType, PaymentClass } from 'generated/graphql';
import { mapCardBrandToCreditCardType } from 'helpers/offlineTerminal/mapCardBrandToMenuX';

export type HotelCardDebtor = {
	RegistrationID?: string;
	GuestType?: string;
	GuestStatus?: string;
	DebtorName?: string;
	CreditBalance?: number | string;
	[key: string]: unknown;
};

/** Same mapping as creditCardEnum() in Payment.tsx / BillSettlement.tsx */
export function creditCardLabelToEnum(
	label?: string | null,
): CreditCardType | undefined {
	if (!label) return undefined;

	if (label === 'American Express Card') {
		return CreditCardType.AmericanExpressCard;
	}
	if (label === 'Diners Card') return CreditCardType.DinersCard;
	if (label === 'JCB Card') return CreditCardType.JcbCard;
	if (label === 'Master Card') return CreditCardType.Mastercard;
	if (label === 'UNION PAY') return CreditCardType.UnionpayCard;
	if (label === 'Visa Card') return CreditCardType.VisaCard;
	if (label === 'Debit Card') return CreditCardType.DebitCard;
	if (label === 'E-Wallet') return CreditCardType.Ewallet;

	return mapCardBrandToCreditCardType(label) ?? undefined;
}

function brandEnum(value?: string | null): CreditCardType | undefined {
	if (!value) return undefined;
	if (Object.values(CreditCardType).includes(value as CreditCardType)) {
		return value as CreditCardType;
	}
	return creditCardLabelToEnum(value) ?? mapCardBrandToCreditCardType(value);
}

function isKnownCardBrand(value: string): boolean {
	return brandEnum(value) != null;
}

export function hotelCardDebtors(
	list: HotelCardDebtor[] | null | undefined,
): HotelCardDebtor[] {
	return (list ?? []).filter(
		row => row?.GuestStatus === 'CARD' && !!row?.RegistrationID,
	);
}

function uniqueBrandMatch(
	list: HotelCardDebtor[],
	cardValue: string,
): HotelCardDebtor | undefined {
	const wanted = brandEnum(cardValue);
	const matches = list.filter(row => {
		const guest = String(row?.GuestType ?? '');
		if (!guest) return false;
		if (guest === cardValue) return true;
		const guestBrand = brandEnum(guest);
		return !!wanted && !!guestBrand && guestBrand === wanted;
	});
	return matches.length === 1 ? matches[0] : undefined;
}

/** HotelX CARD debtor for the selected card type, when it can be identified. */
export function findHotelCardDebtor(
	debtors: HotelCardDebtor[] | null | undefined,
	cardValue: unknown,
): HotelCardDebtor | undefined {
	const list = debtors ?? [];
	if (cardValue == null || cardValue === '') return undefined;

	if (typeof cardValue === 'object') {
		const selected = cardValue as HotelCardDebtor;
		if (selected.RegistrationID) {
			return (
				list.find(row => row.RegistrationID === selected.RegistrationID) ??
				selected
			);
		}
		if (selected.GuestType) {
			return uniqueBrandMatch(list, selected.GuestType);
		}
		return undefined;
	}

	if (typeof cardValue === 'string') {
		const byId = list.find(row => row.RegistrationID === cardValue);
		if (byId) return byId;
		return uniqueBrandMatch(list, cardValue);
	}

	return undefined;
}

/**
 * Resolve creditCardType for GraphQL paymentInput from ECR or manual form values.
 */
export function resolvePaymentCreditCardType(
	paymentRow: Record<string, unknown> | undefined,
	formCardType: unknown,
): CreditCardType | undefined {
	if (
		String(paymentRow?.paymentClass || '').toUpperCase() !== PaymentClass.Credit
	) {
		return undefined;
	}

	if (paymentRow?.creditCardType) {
		const rowType = paymentRow.creditCardType;
		if (Object.values(CreditCardType).includes(rowType as CreditCardType)) {
			return rowType as CreditCardType;
		}
		return (
			creditCardLabelToEnum(String(rowType)) ??
			mapCardBrandToCreditCardType(String(rowType)) ??
			undefined
		);
	}

	if (
		formCardType != null &&
		typeof formCardType === 'object' &&
		(formCardType as { GuestType?: string }).GuestType != null
	) {
		return creditCardLabelToEnum(
			(formCardType as { GuestType?: string }).GuestType,
		);
	}

	if (!formCardType) return undefined;

	if (Object.values(CreditCardType).includes(formCardType as CreditCardType)) {
		return formCardType as CreditCardType;
	}

	if (typeof formCardType === 'string') {
		return (
			creditCardLabelToEnum(formCardType) ??
			mapCardBrandToCreditCardType(formCardType) ??
			undefined
		);
	}

	return undefined;
}

/**
 * HotelX RegistrationID for Schedule Billing.
 * Accepts the CARD debtor object, its RegistrationID, or a single matching card brand.
 */
export function registrationIDFromCreditCardType(
	formCardType: unknown,
	cardDebtors?: HotelCardDebtor[] | null,
): string | undefined {
	if (
		formCardType != null &&
		typeof formCardType === 'object' &&
		(formCardType as { RegistrationID?: string }).RegistrationID
	) {
		return (formCardType as { RegistrationID?: string }).RegistrationID;
	}

	const matched = findHotelCardDebtor(cardDebtors, formCardType);
	if (matched?.RegistrationID) return matched.RegistrationID;

	if (
		typeof formCardType === 'string' &&
		formCardType.trim() &&
		!isKnownCardBrand(formCardType)
	) {
		return formCardType;
	}

	return undefined;
}

/** Replace a card brand with the HotelX CARD debtor before payment is saved. */
export function withResolvedCreditAccount<T extends { cardType?: unknown }>(
	formData: T,
	cardDebtors?: HotelCardDebtor[] | null,
): T {
	const debtor = findHotelCardDebtor(cardDebtors, formData?.cardType);
	if (!debtor?.RegistrationID) return formData;
	return { ...formData, cardType: debtor };
}

export function creditCardDropdownMode(
	integrateCityLedger: boolean | undefined,
	isDebtorListLoading: boolean,
	debtorListCalled: boolean | undefined,
	cardDebtors: HotelCardDebtor[],
): 'local' | 'loading' | 'empty' | 'hotel' {
	if (integrateCityLedger !== true) return 'local';
	if (!debtorListCalled || isDebtorListLoading) return 'loading';
	if (!cardDebtors.length) return 'empty';
	return 'hotel';
}

/** While city ledger is on, wait for debtor list before showing card options / confirm. */
export function isCreditPaymentWaitingForDebtorList(
	integrateCityLedger: boolean | undefined,
	isDebtorListLoading: boolean,
	debtorListCalled?: boolean,
): boolean {
	if (integrateCityLedger !== true) return false;
	if (debtorListCalled === false) return true;
	return isDebtorListLoading;
}
